import { ForwardRefExoticComponent } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import { ISvgIconProps } from 'transfix-ui/components/SvgIcon'
import { IShipmentMissingInfo, IShipmentStatus, Maybe } from 'types/FMSGraphqlTypes'
import { LOAD_STATE, TAB } from 'apps/fleet-planner/constants/loads'
import { ILoadFilters } from 'apps/fleet-planner/types'
import {
  TrackingFilledBlueIcon,
  TrackingFilledGrayIcon,
  TrackingFilledGrayStrikethroughIcon,
  TrackingFilledRedIcon,
  TrackingFilledYellowIcon,
} from 'transfix-icons'
import { IShipmentsQueryVariables } from './graphql/Shipments'

export const SHOW_DOCS_TABS = [TAB.ALL, TAB.DELIVERED, TAB.INVOICED, TAB.ARCHIVED]

export enum POD_STATES {
  ACCEPTED = 'accepted',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PENDING = 'pending',
  NEEDED = 'needed',
  MISSING = 'missing',
}

export const getPodState = (
  podUploaded: boolean,
  podState: Maybe<string>,
  missingInfo: Maybe<IShipmentMissingInfo[]>,
  isExternal: boolean
) => {
  if (!isExternal) return podUploaded ? POD_STATES.ACCEPTED : POD_STATES.MISSING
  if (podState === 'pod_approved') return POD_STATES.APPROVED
  if (missingInfo?.includes(IShipmentMissingInfo.PodRejected)) return POD_STATES.REJECTED
  if (missingInfo?.includes(IShipmentMissingInfo.PodMissing)) return POD_STATES.NEEDED
  return podUploaded ? POD_STATES.PENDING : POD_STATES.MISSING
}

export const mergeFilters = (
  prevState: IShipmentsQueryVariables,
  newState: Partial<ILoadFilters>
) => {
  const objectFilter = cloneDeep(prevState)

  if ('maxAge' in newState) {
    objectFilter.maxAge = newState.maxAge
  }

  if ('referenceNumber' in newState) {
    objectFilter.referenceNumber = newState.referenceNumber
  }

  if ('pickupDate' in newState) {
    objectFilter.pickupDate = newState.pickupDate
  }

  if ('dropoffDate' in newState) {
    objectFilter.dropoffDate = newState.dropoffDate
  }

  if ('customerId' in newState) {
    objectFilter.customerId = newState.customerId
  }

  if ('pickupSubdivisionCode' in newState) {
    objectFilter.pickupSubdivisionCode = newState.pickupSubdivisionCode
  }

  if ('dropoffSubdivisionCode' in newState) {
    objectFilter.dropoffSubdivisionCode = newState.dropoffSubdivisionCode
  }

  if ('driverId' in newState) {
    if (newState.driverId === undefined) {
      objectFilter.driverId = undefined
    } else {
      objectFilter.driverId = parseInt(newState.driverId, 10)
    }
  }

  if ('dispatcherId' in newState) {
    if (newState.dispatcherId === undefined) {
      objectFilter.dispatcherId = undefined
    } else {
      objectFilter.dispatcherId = parseInt(newState.dispatcherId, 10)
    }
  }

  if ('motorCarrierId' in newState) {
    objectFilter.motorCarrierId = newState.motorCarrierId
  }
  return objectFilter
}

const POST_ACTIVE_LOAD_STATUSES = [
  LOAD_STATE.Delivered,
  LOAD_STATE.Invoiced,
  LOAD_STATE.Completed,
  LOAD_STATE.Cancelled,
  LOAD_STATE.TONU,
  LOAD_STATE.Claim,
  LOAD_STATE['Payment Pending'],
  LOAD_STATE['POD Rejected'],
]

export enum TRACKING_STATUSES {
  NOT_ACTIVE = 'not_active',
  GET_READY = 'get_ready',
  TRACKING = 'tracking',
  LATE = 'late',
  NOT_TRACKING = 'not_tracking',
}

export const TRACKING_STATUS_TO_ICON: Record<
  TRACKING_STATUSES,
  ForwardRefExoticComponent<ISvgIconProps & React.RefAttributes<SVGSVGElement>>
> = {
  [TRACKING_STATUSES.NOT_ACTIVE]: TrackingFilledGrayStrikethroughIcon,
  [TRACKING_STATUSES.GET_READY]: TrackingFilledGrayIcon,
  [TRACKING_STATUSES.TRACKING]: TrackingFilledBlueIcon,
  [TRACKING_STATUSES.LATE]: TrackingFilledYellowIcon,
  [TRACKING_STATUSES.NOT_TRACKING]: TrackingFilledRedIcon,
}

export const TRACKING_STATUS_TO_TOOLTIP: Record<TRACKING_STATUSES, string> = {
  [TRACKING_STATUSES.NOT_ACTIVE]: 'Not Active',
  [TRACKING_STATUSES.GET_READY]: 'Get Ready to Track',
  [TRACKING_STATUSES.TRACKING]: 'Tracking',
  [TRACKING_STATUSES.LATE]: 'Running Late',
  [TRACKING_STATUSES.NOT_TRACKING]: 'Not Tracking',
}

export const getTrackingStatus = (
  isTracking: boolean,
  onTime: boolean,
  shouldBeTracking: boolean,
  status: string
) => {
  if (POST_ACTIVE_LOAD_STATUSES.includes(status as LOAD_STATE)) {
    return TRACKING_STATUSES.NOT_ACTIVE
  }

  if (!shouldBeTracking) {
    return TRACKING_STATUSES.GET_READY
  }

  if (isTracking) {
    return onTime ? TRACKING_STATUSES.TRACKING : TRACKING_STATUSES.LATE
  }

  return TRACKING_STATUSES.NOT_TRACKING
}

export const headerStatusCountRequest = [
  { internal: ['all'], external: ['all'] },
  { internal: [IShipmentStatus.pending], external: [IShipmentStatus.pending] },
  {
    internal: [IShipmentStatus.ready_for_dispatch],
    external: [IShipmentStatus.ready_for_dispatch],
  },
  {
    internal: [
      IShipmentStatus.dispatched,
      IShipmentStatus.at_pickup,
      IShipmentStatus.loading,
      IShipmentStatus.in_transit,
      IShipmentStatus.in_yard,
      IShipmentStatus.at_dropoff,
      IShipmentStatus.unloading,
      IShipmentStatus.claim,
    ],
    external: [
      IShipmentStatus.dispatched,
      IShipmentStatus.at_pickup,
      IShipmentStatus.in_transit,
      IShipmentStatus.at_dropoff,
    ],
  },
  {
    internal: [IShipmentStatus.delivered, IShipmentStatus.tonu],
    external: [
      IShipmentStatus.delivered,
      IShipmentStatus.payment_pending,
      IShipmentStatus.pod_rejected,
      IShipmentStatus.invoiced,
      IShipmentStatus.completed,
      IShipmentStatus.tonu,
    ],
  },
  { internal: [IShipmentStatus.invoiced] },
  {
    internal: [IShipmentStatus.completed, IShipmentStatus.cancelled],
    external: [IShipmentStatus.cancelled],
  },
]

export const TAB_EXTERNAL_STATUS_MAP: { [id: string]: [] | IShipmentStatus[] } = {
  [TAB.ALL]: [],
  [TAB.PENDING]: [IShipmentStatus.pending],
  [TAB.READY_FOR_DISPATCH]: [IShipmentStatus.ready_for_dispatch],
  [TAB.ACTIVE]: [
    IShipmentStatus.dispatched,
    IShipmentStatus.in_transit,
    IShipmentStatus.at_pickup,
    IShipmentStatus.loading,
    IShipmentStatus.in_yard,
    IShipmentStatus.at_dropoff,
    IShipmentStatus.unloading,
    IShipmentStatus.tonu,
    IShipmentStatus.claim,
  ],
  [TAB.DELIVERED]: [
    IShipmentStatus.delivered,
    IShipmentStatus.payment_pending,
    IShipmentStatus.pod_rejected,
    IShipmentStatus.invoiced,
    IShipmentStatus.completed,
    IShipmentStatus.tonu,
  ],
  [TAB.INVOICED]: [IShipmentStatus.invoiced],
  [TAB.ARCHIVED]: [IShipmentStatus.cancelled, IShipmentStatus.completed],
}
